/**
 * "Does this need a label?": decision rules based on art. 50(4) of the EU AI Act (deployer obligations).
 * Shared by the dashboard, live labelling mode, the REST API and MCP so every place gives the same answer.
 * Not legal advice: the result explains the reasoning and is stored with the label as documentation.
 */

export type AssessKind = "TEXT" | "IMAGE" | "VIDEO";

export interface AssessAnswers {
  kind: AssessKind;
  /** TEXT: how much the AI contributed */
  aiShare?: "generated" | "substantial" | "minor";
  /** TEXT: published to inform the public on matters of public interest (news, health, law, finance, politics…) */
  publicInterest?: boolean;
  /** TEXT: a human reviewed or edited the text before publication */
  humanReview?: boolean;
  /** TEXT: an identifiable person or company holds editorial responsibility */
  editorialResponsibility?: boolean;
  /** IMAGE/VIDEO: realistic enough to be taken for real people, places, objects or events */
  realistic?: "yes" | "no" | "unsure";
  /** IMAGE/VIDEO: shows real-looking people */
  people?: boolean;
  /** IMAGE/VIDEO: evidently artistic, creative, satirical or fictional */
  artistic?: boolean;
  /** Site industry: adds sector-specific questions and warnings */
  industry?: "general" | "medical" | "finance_legal" | "news" | "ecommerce";
  /** IMAGE/VIDEO, medical: shows patients, medical staff, treatments, clinics or treatment results */
  clinical?: boolean;
}

/** label: visible label required · label_discreet: required, may be unobtrusive · documented_review: editorial exemption, keep the approval on record · recommended / not_required: no legal duty */
export type AssessOutcome = "label" | "label_discreet" | "documented_review" | "recommended" | "not_required";

export interface Assessment {
  outcome: AssessOutcome;
  /** Whether a visible label is legally required under art. 50(4) */
  required: boolean;
  basis: string;
  reason: { pl: string; en: string };
  warnings: Array<{ pl: string; en: string }>;
}

export const OUTCOME_LABEL: Record<AssessOutcome, { pl: string; en: string }> = {
  label: { pl: "Oznacz widocznie", en: "Label visibly" },
  label_discreet: { pl: "Oznacz (może być dyskretnie)", en: "Label (may be discreet)" },
  documented_review: { pl: "Wystarczy udokumentowana akceptacja", en: "Documented approval is enough" },
  recommended: { pl: "Nie wymaga (zalecane)", en: "Not required (recommended)" },
  not_required: { pl: "Nie wymaga oznaczenia", en: "No label required" },
};

export function assess(a: AssessAnswers): Assessment {
  const r = assessBase(a);
  const medical = a.industry === "medical";
  if (medical && a.kind !== "TEXT" && a.clinical) {
    r.warnings.unshift({
      pl: "Branża medyczna: obraz pacjentów, personelu, zabiegów lub ich efektów wygenerowany przez AI może wprowadzić pacjentów w błąd (ryzyko deepfake). Oznacz go widocznie i nie przedstawiaj jako rzeczywistego efektu leczenia.",
      en: "Healthcare: AI images of patients, staff, treatments or their results can mislead patients (deep fake risk). Label them visibly and never present them as real treatment results.",
    });
    if (r.outcome !== "label")
      return {
        ...r,
        outcome: "label",
        required: r.required,
        basis: `${r.basis}; AIMARKER healthcare guidance`,
        reason: {
          pl: `${r.reason.pl} W branży medycznej zalecamy jednak widoczne oznaczenie takich obrazów.`,
          en: `${r.reason.en} In healthcare we still recommend a visible label for such images.`,
        },
      };
  }
  if (a.kind === "TEXT" && (medical || a.industry === "finance_legal") && r.outcome === "documented_review")
    r.warnings.push({
      pl: "Wyjątek redakcyjny wystarcza prawnie, ale w treściach o zdrowiu, prawie i finansach zalecamy także widoczną etykietę (np. łagodną: „zweryfikowane przez specjalistę”).",
      en: "The editorial exemption is legally sufficient, but for health, legal and financial content we also recommend a visible label (e.g. a gentle one: “verified by a specialist”).",
    });
  return r;
}

function assessBase(a: AssessAnswers): Assessment {
  const warnings: Assessment["warnings"] = [];

  if (a.kind === "TEXT") {
    if (a.aiShare === "minor")
      return {
        outcome: "not_required",
        required: false,
        basis: "AI Act art. 50(4) §2",
        reason: {
          pl: "AI pełniło tylko funkcję pomocniczą (korekta, styl, tłumaczenie bez zmiany sensu). Tekst nie jest wygenerowany ani istotnie zmieniony przez AI, więc nie wymaga oznaczenia.",
          en: "AI only played an assistive role (proofreading, style, translation without changing the meaning). The text is not generated or substantially altered by AI, so no label is required.",
        },
        warnings,
      };
    if (a.publicInterest === false)
      return {
        outcome: "recommended",
        required: false,
        basis: "AI Act art. 50(4) §2",
        reason: {
          pl: "Tekst nie służy informowaniu opinii publicznej o sprawach interesu publicznego (np. opis produktu, oferta, reklama). Art. 50 ust. 4 nie wymaga oznaczenia; dobrowolna etykieta buduje zaufanie.",
          en: "The text does not inform the public on matters of public interest (e.g. a product description, offer or ad). Art. 50(4) does not require a label; a voluntary label builds trust.",
        },
        warnings,
      };
    if (a.humanReview && a.editorialResponsibility)
      return {
        outcome: "documented_review",
        required: false,
        basis: "AI Act art. 50(4) §2 (editorial exemption)",
        reason: {
          pl: "Tekst przeszedł przegląd człowieka, a odpowiedzialność redakcyjną ponosi wskazana osoba lub firma. Zachodzi wyjątek redakcyjny: widoczna etykieta nie jest wymagana, ale akceptację trzeba udokumentować (redakcja lub klient).",
          en: "The text was reviewed by a human and a named person or company holds editorial responsibility. The editorial exemption applies: a visible label is not required, but the approval must be documented (editor or client).",
        },
        warnings,
      };
    if (a.humanReview && !a.editorialResponsibility)
      warnings.push({
        pl: "Sam przegląd nie wystarczy: do wyjątku potrzebna jest też osoba lub firma ponosząca odpowiedzialność redakcyjną.",
        en: "A review alone is not enough: the exemption also needs a person or company holding editorial responsibility.",
      });
    return {
      outcome: "label",
      required: true,
      basis: "AI Act art. 50(4) §2",
      reason: {
        pl: "Tekst wygenerowany lub istotnie zmieniony przez AI informuje opinię publiczną o sprawach interesu publicznego i nie ma udokumentowanej kontroli redakcyjnej. Wymaga widocznego oznaczenia.",
        en: "AI-generated or substantially AI-altered text informs the public on matters of public interest and has no documented editorial control. It requires a visible label.",
      },
      warnings,
    };
  }

  // Images, video, audio: deep fake test
  const realistic = a.realistic !== "no";
  if (a.realistic === "unsure")
    warnings.push({ pl: "Przy wątpliwościach przyjmujemy, że treść może uchodzić za prawdziwą.", en: "When in doubt we assume the content may pass as real." });
  if (realistic && a.people)
    warnings.push({
      pl: "Realistyczny wizerunek ludzi: wysokie ryzyko wprowadzenia w błąd. Sprawdź też zgody i prawa do wizerunku.",
      en: "Realistic depiction of people: high risk of misleading. Also check consents and image rights.",
    });
  if (!realistic)
    return {
      outcome: "recommended",
      required: false,
      basis: "AI Act art. 50(4) §1",
      reason: {
        pl: "Obraz nie przypomina realnych osób, miejsc, przedmiotów ani zdarzeń w sposób, który mógłby wprowadzić w błąd, więc nie jest deepfake'iem. Oznaczenie jest zalecane (Kodeks praktyk UE), ale nie wymagane.",
        en: "The image does not resemble real people, places, objects or events in a way that could mislead, so it is not a deep fake. A label is recommended (EU Code of Practice) but not required.",
      },
      warnings,
    };
  if (a.artistic)
    return {
      outcome: "label_discreet",
      required: true,
      basis: "AI Act art. 50(4) §1",
      reason: {
        pl: "Treść przypomina rzeczywistość, ale ma oczywiście artystyczny, satyryczny lub fikcyjny charakter. Ujawnienie jest wymagane, ale może być dyskretne, tak by nie utrudniać odbioru dzieła.",
        en: "The content resembles reality but is evidently artistic, satirical or fictional. Disclosure is required but may be unobtrusive so it does not hamper the work.",
      },
      warnings,
    };
  return {
    outcome: "label",
    required: true,
    basis: "AI Act art. 50(4) §1",
    reason: {
      pl: "Realistyczna treść, która może zostać uznana za prawdziwą (deepfake). Wymaga widocznego oznaczenia, że została wygenerowana lub zmanipulowana przez AI.",
      en: "Realistic content that could be taken as real (deep fake). It requires a visible disclosure that it was generated or manipulated by AI.",
    },
    warnings,
  };
}

/** Normalises untrusted input (API, snippet) into answers; unknown fields are dropped. */
export function parseAnswers(raw: unknown): AssessAnswers | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  const kind = r.kind === "TEXT" || r.kind === "IMAGE" || r.kind === "VIDEO" ? r.kind : r.kind === "ARTICLE" ? "TEXT" : null;
  if (!kind) return null;
  const bool = (v: unknown) => (typeof v === "boolean" ? v : undefined);
  return {
    kind,
    aiShare: r.aiShare === "generated" || r.aiShare === "substantial" || r.aiShare === "minor" ? r.aiShare : undefined,
    publicInterest: bool(r.publicInterest),
    humanReview: bool(r.humanReview),
    editorialResponsibility: bool(r.editorialResponsibility),
    realistic: r.realistic === "yes" || r.realistic === "no" || r.realistic === "unsure" ? r.realistic : undefined,
    people: bool(r.people),
    artistic: bool(r.artistic),
    industry: r.industry === "medical" || r.industry === "finance_legal" || r.industry === "news" || r.industry === "ecommerce" || r.industry === "general" ? r.industry : undefined,
    clinical: bool(r.clinical),
  };
}
